import { RefObject, useLayoutEffect, useState } from "react";

const SMALL_PHONE_QUERY = "(max-width: 390px) and (max-height: 700px)";
const SMALL_PHONE_FONT_SCALE = 0.9;

/**
 * Finds the largest font size at which `target` fits inside `box`.
 *
 * Extracted so the read-only option text and the editable one measure
 * identically — the writing screen and the turn screen must render an option at
 * the same size, or a player writes to one layout and reads another.
 *
 * Binary search rather than shrink-by-one: ~6 measurements instead of up to 50,
 * which matters on every keystroke on a phone.
 */
export const useFitToBox = (
  boxRef: RefObject<HTMLElement>,
  targetRef: RefObject<HTMLElement>,
  text: string,
  { min = 14, max = 40 }: { min?: number; max?: number } = {},
) => {
  const [size, setSize] = useState(max);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const target = targetRef.current;
    if (!box || !target) return;

    const fit = () => {
      let low = min;
      let high = max;
      let best = min;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        target.style.fontSize = `${mid}px`;

        const fits =
          target.scrollHeight <= box.clientHeight &&
          target.scrollWidth <= box.clientWidth;

        if (fits) {
          best = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      // Keep the normal fit calculation, then give SE-sized screens another
      // 10% of breathing room. Scaling during the search would merely let the
      // algorithm choose a larger candidate and cancel out the reduction.
      const scale = window.matchMedia(SMALL_PHONE_QUERY).matches
        ? SMALL_PHONE_FONT_SCALE
        : 1;
      const displaySize = best * scale;
      target.style.fontSize = `${displaySize}px`;
      setSize(displaySize);
    };

    fit();

    // Rotation, the on-screen keyboard and reveal transitions all resize the box.
    const observer = new ResizeObserver(fit);
    observer.observe(box);

    /*
     * Re-fit once the web font has actually arrived.
     *
     * The measurement above runs against whatever font is available at layout
     * time. On a cold load that is the fallback, whose metrics differ from
     * Space Grotesk — so the chosen size is wrong the moment the real font
     * swaps in, and the text overflows its box and is clipped.
     *
     * The ResizeObserver cannot catch this: loading a font changes the size of
     * the *text*, not of the box being observed. And it never reproduces
     * locally, where the font is already cached before first paint.
     */
    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) fit();
    });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [boxRef, targetRef, text, min, max]);

  return size;
};

/** Shared between the read-only and editable option text. */
export const FIT_TEXT_CLASS = "block text-balance text-center font-medium";
export const FIT_LINE_HEIGHT = 1.15;
